import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { workoutService } from "@/services/workout";
import { toast } from "sonner";
import { ExerciseResult, Workout, WorkoutLog, WorkoutLogSchema } from "@/schemas/workout";
import { z } from "zod";
import { useWorkoutSounds } from "@/hooks/useWorkoutSounds";
import { generateId } from "@/utils/id";
import { useVoiceRecognition } from "@/hooks/useVoiceRecognition";

export type WorkoutState = "loading" | "ready" | "active" | "complete" | "error";

export function useActiveWorkoutSession() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Workout state
  const [workout, setWorkout] = useState<Workout | null>(null);
  const [workoutState, setWorkoutState] = useState<WorkoutState>("loading");
  const [workoutLog, setWorkoutLog] = useState<z.infer<typeof WorkoutLogSchema> | null>(null);
  const [exerciseResults, setExerciseResults] = useState<Record<string, ExerciseResult>>({});
  const [elapsedTime, setElapsedTime] = useState(0);

  // Exercise state
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [currentSetIndex, setCurrentSetIndex] = useState(0);
  const [restMode, setRestMode] = useState(false);
  const [currentRestTime, setCurrentRestTime] = useState(0);
  const [workoutProgress, setWorkoutProgress] = useState(0);
  const [isRestAfterExercise, setIsRestAfterExercise] = useState(false);

  // UI state
  const [debugVolume, setDebugVolume] = useState(0.5);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [recognizedCommand, setRecognizedCommand] = useState("");
  const [showCommandFeedback, setShowCommandFeedback] = useState(false);

  // Custom hooks
  const { soundEnabled, toggleSound, playSound } = useWorkoutSounds();

  // Pause state for workout session
  const [isPaused, setIsPaused] = useState(false);
  const pauseWorkout = useCallback(() => setIsPaused(true), []);
  const resumeWorkout = useCallback(() => setIsPaused(false), []);

  // Derived state
  const currentExercise = workout?.exercises[currentExerciseIndex] || null;
  const isLastExercise = currentExerciseIndex === (workout?.exercises.length || 0) - 1;
  const totalSets = useMemo(() => {
    if (!currentExercise) return 1;
    switch (currentExercise.structure.type) {
      case "sets":
      case "timed":
      case "cadence":
        return currentExercise.structure.sets;
      default:
        return 1;
    }
  }, [currentExercise]);
  const isLastSet = currentSetIndex === totalSets - 1;
  const workoutComplete = isLastExercise && isLastSet && !restMode;

  // Snapshot
  const generateSnapshot = useCallback(() => {
    if (!workout) throw new Error("Workout is null in getSnapshot");
    return {
      exercise_results: exerciseResults,
      exercises: workout.exercises,
      id: workoutLog?.id || generateId(),
      user_id: workoutLog?.user_id || workout.user_id,
      description: workout.description,
    };
  }, [workout, workoutLog, exerciseResults]);

  // Progress update utility
  const updateWorkoutProgress = useCallback(() => {
    if (!workout) throw new Error("Workout is null in updateWorkoutProgress");

    const totalSetsAll = workout.exercises.reduce((sum, ex) => {
      let exSets = ["sets", "timed", "cadence"].includes(ex.structure.type)
        ? ex.structure.sets
        : 1;
      return sum + exSets;
    }, 0);

    const completedSets = workout.exercises
      .slice(0, currentExerciseIndex)
      .reduce((sum, ex) => {
        let exSets = ["sets", "timed", "cadence"].includes(ex.structure.type)
          ? ex.structure.sets
          : 1;
        return sum + exSets;
      }, 0);

    const progress = Math.round(((completedSets + currentSetIndex) / totalSetsAll) * 100);
    setWorkoutProgress(progress);
  }, [workout, currentExerciseIndex, currentSetIndex]);

  // Persist progress
  const saveWorkoutProgress = useCallback(async () => {
    if (!workoutLog) return;
    try {
      await workoutService.updateWorkoutLog(workoutLog.id, {
        workoutSnapshot: generateSnapshot(),
      });
    } catch {
      // swallow
    }
  }, [workoutLog, generateSnapshot]);

  // Complete set
  const completeSet = useCallback(() => {
    if (!currentExercise || !workoutLog) return;
    if (soundEnabled) {
      try { playSound("complete"); } catch {}
    }
    const updated = {
      ...exerciseResults,
      [currentExercise.id]: {
        ...exerciseResults[currentExercise.id],
        sets_completed: (exerciseResults[currentExercise.id].sets_completed || 0) + 1,
        actual_sets: (exerciseResults[currentExercise.id].actual_sets || 0) + 1,
        completed: (exerciseResults[currentExercise.id].sets_completed || 0) + 1 >= totalSets,
      },
    };
    setExerciseResults(updated);
    saveWorkoutProgress();
    if (isLastSet) {
      if (isLastExercise) {
        setWorkoutState("complete");
      } else {
        setCurrentExerciseIndex(prev => prev + 1);
        setCurrentSetIndex(0);
        setCurrentRestTime(currentExercise.rest_time);
        setRestMode(true);
      }
    } else {
      setCurrentSetIndex(prev => prev + 1);
      setCurrentRestTime(currentExercise.rest_time);
      setRestMode(true);
    }
    updateWorkoutProgress();
  }, [currentExercise, workoutLog, soundEnabled, playSound, exerciseResults, isLastSet, isLastExercise, totalSets, saveWorkoutProgress, updateWorkoutProgress]);

  // Skip exercise
  const skipExercise = useCallback(() => {
    if (isLastExercise) setWorkoutState("complete");
    else {
      setCurrentExerciseIndex(prev => prev + 1);
      setCurrentSetIndex(0);
      setRestMode(false);
    }
    updateWorkoutProgress();
  }, [isLastExercise, updateWorkoutProgress]);

  // Skip rest
  const skipRestCommand = useCallback(() => {
    if (restMode) setRestMode(false);
  }, [restMode]);

  // Rest complete handler
  const handleRestComplete = useCallback(() => {
    setRestMode(false);
    if (soundEnabled) {
      try { playSound("rest"); } catch {}
    }
  }, [soundEnabled, playSound]);

  // Start workout
  const startWorkout = useCallback(async () => {
    if (!workout) return;
    const log = await workoutService.startWorkoutLog(workout.id, {
      id: generateId(),
      user_id: workout.user_id,
      workout_id: workout.id,
      workout_name: workout.name,
      status: "in_progress",
      workout_snapshot: generateSnapshot(),
    });
    setWorkoutLog(log);
    setWorkoutState("active");
    const init: Record<string, ExerciseResult> = {};
    workout.exercises.forEach(ex => {
      init[ex.id] = { ...ex, completed: false, sets_completed: 0 };
    });
    setExerciseResults(init);
    updateWorkoutProgress();
    saveWorkoutProgress();
  }, [workout, generateSnapshot, updateWorkoutProgress, saveWorkoutProgress]);

  // Complete workout
  const completeWorkout = useCallback(async () => {
    if (!workoutLog) return;
    await workoutService.completeWorkoutLog(workoutLog.id, {
      workoutSnapshot: generateSnapshot(),
    });
    toast.success("Workout completed!");
    navigate("/activity");
  }, [workoutLog, navigate, generateSnapshot]);

  // Load workout on mount
  useEffect(() => {
    async function load() {
      if (!id) { setWorkoutState("error"); toast.error("No workout ID"); return; }
      try {
        const w = await workoutService.getWorkout(id);
        if (!w) { setWorkoutState("error"); toast.error("Not found"); return; }
        setWorkout(w);
        setWorkoutState("ready");
      } catch {
        setWorkoutState("error"); toast.error("Failed load");
      }
    }
    load();
  }, [id]);

  // Auto-save
  useEffect(() => {
    if (workoutState !== "active" || !workoutLog) return;
    const iv = setInterval(saveWorkoutProgress, 30000);
    return () => clearInterval(iv);
  }, [workoutState, workoutLog, saveWorkoutProgress]);

  // Visibility change save
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "hidden" && workoutState === "active") {
        saveWorkoutProgress();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [workoutState, saveWorkoutProgress]);

  // Timer for elapsed
  useEffect(() => {
    let iv: NodeJS.Timeout;
    if (workoutState === "active") {
      iv = setInterval(() => setElapsedTime(e => e + 1), 1000);
    }
    return () => iv && clearInterval(iv);
  }, [workoutState]);

  // Update isRestAfterExercise based on state transitions
  useEffect(() => {
    setIsRestAfterExercise(restMode && isLastSet && !workoutComplete);
  }, [restMode, isLastSet, workoutComplete]);

  // Voice commands
  const completeSetCommand = useCallback(() => {
    if (!restMode && !workoutComplete) { setRecognizedCommand("Complete Set"); setShowCommandFeedback(true); completeSet(); }
  }, [restMode, workoutComplete, completeSet]);

  const skipExerciseCommand = useCallback(() => {
    if (!restMode && !workoutComplete) { setRecognizedCommand("Skip Exercise"); setShowCommandFeedback(true); skipExercise(); }
  }, [restMode, workoutComplete, skipExercise]);

  const skipRestVoice = useCallback(() => {
    if (restMode) { setRecognizedCommand("Skip Rest"); setShowCommandFeedback(true); setRestMode(false); }
  }, [restMode]);

  const toggleSoundCommand = useCallback(() => {
    setRecognizedCommand(soundEnabled ? "Sound Off" : "Sound On");
    setShowCommandFeedback(true);
    toggleSound();
  }, [soundEnabled, toggleSound]);

  const commandMapping = useMemo(() => ({
    completeSet: { phrases:["complete set","done","finished","next set","complete"], action: completeSetCommand, description:"Complete the current set" },
    skipExercise: { phrases:["skip exercise","next exercise","skip"], action: skipExerciseCommand, description:"Skip to next exercise" },
    skipRest: { phrases:["skip rest","end rest","no rest"], action: skipRestVoice, description:"Skip the rest period" },
    toggleSound: { phrases:["toggle sound","sound on","sound off","mute","unmute"], action: toggleSoundCommand, description:"Toggle sound on/off" },
  }), [completeSetCommand, skipExerciseCommand, skipRestVoice, toggleSoundCommand]);

  const voiceRecognitionEnabled = useMemo(() => voiceEnabled, [voiceEnabled]);
  const { isListening, transcript, recentTranscripts: voiceTranscripts, status: voiceStatus, toggleListening, permissionDenied } = useVoiceRecognition(commandMapping, voiceRecognitionEnabled);

  const toggleVoice = useCallback(() => {
    if (permissionDenied) { toast.error("Enable mic in settings."); return; }
    setVoiceEnabled(v => !v);
  }, [permissionDenied]);


  // Permission denied toast
  useEffect(() => {
    if (permissionDenied && voiceEnabled) {
      toast.error("Microphone denied.");
      setVoiceEnabled(false);
    }
  }, [permissionDenied, voiceEnabled]);


  // Reset and exit
  const resetWorkout = useCallback(() => {
    setCurrentExerciseIndex(0);
    setCurrentSetIndex(0);
    setRestMode(false);
    setElapsedTime(0);
    setWorkoutProgress(0);
    const init: Record<string,ExerciseResult> = {};
    workout?.exercises.forEach(ex => { init[ex.id] = { ...ex, completed:false, sets_completed:0 }; });
    setExerciseResults(init);
    updateWorkoutProgress();
  }, [workout, updateWorkoutProgress]);

  const exitWorkout = useCallback(() => navigate("/activity"), [navigate]);

  return {
    workoutState,
    debugMode: false,
    workout,
    workoutLog,
    exerciseResults,
    elapsedTime,
    onStartWorkout: startWorkout,
    onCompleteWorkout: completeWorkout,
    onNavigateBack: () => navigate("/workouts"),
    currentExerciseIndex,
    currentSetIndex,
    restMode,
    currentRestTime,
    workoutProgress,
    isLastExercise,
    isLastSet,
    totalSets,
    soundEnabled,
    toggleSound,
    playSound,
    voiceEnabled,
    toggleVoice,
    completeSet,
    skipExercise,
    skipRestCommand,
    handleRestComplete,
    resetWorkout,
    exitWorkout,
    debugVolume,
    setDebugVolume,
    isListening,
    transcript,
    voiceTranscripts,
    voiceStatus,
    toggleListening,
    permissionDenied,
    recognizedCommand,
    showCommandFeedback,
    setShowCommandFeedback,
    commandMapping,
    isRestAfterExercise,
    isPaused,
    pauseWorkout,
    resumeWorkout,
  };
}
